import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API_URL } from './App';  // Import from App.js

function EmailVerificationPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    } else {
      setStatus('error');
      setMessage('No verification token provided');
    }
  }, [token]);

  const verifyEmail = async (token) => {
    try {
      const response = await axios.post(`${API_URL}/auth/verify-email`, { token });
      
      if (response.data.success) {
        setStatus('success');
        setMessage('Email verified successfully! You can now log in to your account.');
        
        // Automatically log in the user if they want
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
      }
    } catch (error) {
      const errorData = error.response?.data;
      setStatus('error');
      setMessage(errorData?.error || 'Failed to verify email. Please try again.');
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-16 h-16 text-green-400" />;
      case 'error':
        return <XCircle className="w-16 h-16 text-red-400" />;
      default:
        return <div className="w-16 h-16 border-4 border-purple-400 border-t-transparent rounded-full animate-spin" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-green-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-purple-400';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-8 text-center">
          <div className="flex justify-center mb-6">
            {getStatusIcon()}
          </div>
          
          <h2 className={`text-2xl font-bold mb-4 ${getStatusColor()}`}>
            {status === 'verifying' && 'Verifying Email...'}
            {status === 'success' && 'Email Verified!'}
            {status === 'error' && 'Verification Failed'}
          </h2>
          
          <p className="text-gray-300 mb-8">
            {message}
          </p>
          
          {status === 'error' && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-center space-x-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>What to do next:</span>
              </div>
              <ul className="text-left mt-2 text-sm text-gray-400 space-y-1">
                <li>• Check if the verification link has expired (links expire in 24 hours)</li>
                <li>• Request a new verification email from the login page</li>
                <li>• Contact support if the problem persists</li>
              </ul>
            </div>
          )}
          
          <div className="space-y-4">
            {status === 'success' && (
              <Link
                to="/login"
                className="block w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
              >
                Go to Login
              </Link>
            )}
            
            {status === 'error' && (
              <Link
                to="/login"
                className="block w-full py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
              >
                Go to Login Page
              </Link>
            )}
            
            <Link
              to="/"
              className="block text-sm text-gray-400 hover:text-gray-300"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailVerificationPage;